import * as analytics from '../../../../shared/analytics/analytics.json';
import { Calendar } from '../../../../../types';
import { CalendarIcon } from '@mui/x-date-pickers';
import CalendarAddDialog from './calendar-add-dialog';
import CalendarDeleteDialog from './calendar-delete-dialog';
import CalendarEditDialog from './calendar-edit-dialog';
import CalendarShareDialog from './calendar-share-dialog';
import { useMemo, useState } from 'react';
import { useChangeLogGate } from '../../../../shared/hooks/use-change-log-gate';
import CalendarTransferDialog from './calendar-transfer-dialog';
import CalendarEventsDialog from './events/calendar-events-dialog';
import QuickReportsDialog, {
  QuickReportConfig,
} from '../../../../shared/quick-reports-dialog';
import { calendarColors } from '../../../../utils/calendar-colors';
import CalendarHistoryDialog from './calendar-history-dialog';
import {
  AdministrationRoles,
  CalendarRoles,
  UserRoles,
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import CalendarGridToolbar from './calendar-grid-toolbar';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import ChangeLogRequiredOverlay from '../../../grid-overlay/change-log-required-overlay';
import { useUserSettings } from '../../../../utils/use-user-settings';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Edit from '@mui/icons-material/Edit';
import Share from '@mui/icons-material/Share';
import History from '@mui/icons-material/History';
interface CalendarPageProps {
  userEmail: string;
  primaryDomain: string;
  calendarRoles: string[];
  changeLogRoles: string[];
  administrationRoles: string[];
  data: Calendar[];
  isLoading: boolean;
  isFetching: boolean;
  isAllUsers: boolean;
  fetched?: boolean;
  onOpenChangeLogDialog?: () => void;
}

const reports: QuickReportConfig[] = [
  {
    displayName: 'Abandoned Calendar Events',
    description:
      'Identifies calendar events that were scheduled but not attended or completed.',
    reportType: 'abandonedCalendarsReport',
    role: ToolsRoles.RunCalendarAbandonedEventsReport.value,
    requiresDateRange: true,
  },
  {
    displayName: 'Calendar Access',
    description: 'Details the level of access users have to shared calendars.',
    reportType: 'calendarAccessReport',
    role: ToolsRoles.RunCalendarAccessReport.value,
    requiresDateRange: false,
  },
  {
    displayName: 'Calendar Events',
    description: 'Details scheduled events in the calendar.',
    reportType: 'calendarEventsReport',
    role: ToolsRoles.RunCalendarReport.value,
    requiresDateRange: true,
  },
];

export default function CalendarGrid({
  userEmail,
  calendarRoles,
  primaryDomain,
  changeLogRoles,
  administrationRoles,
  data,
  isLoading,
  isFetching,
  isAllUsers,
  fetched,
  onOpenChangeLogDialog,
}: CalendarPageProps) {
  const [openAddCalendar, setOpenAddCalendar] = useState(false);
  const [openDeleteCalendars, setOpenDeleteCalendars] = useState(false);
  const [openEditCalendar, setOpenEditCalendar] = useState(false);
  const [openShareCalendar, setOpenShareCalendar] = useState(false);
  const [openTransferCalendar, setOpenTransferCalendar] = useState(false);
  const [openEventsCalendar, setOpenEventsCalendar] = useState(false);
  const [openCalendarHistory, setOpenCalendarHistory] = useState(false);
  const [openQuickReports, setOpenQuickReports] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState<Calendar | null>(
    null
  );
  const { viewType } = useUserSettings();
  const eventsGate = useChangeLogGate({
    permissionValue: CalendarRoles.ViewCalendarEvents.value,
    changeLogRoles,
    initialOpen: false,
  });
  const [pendingEventsCalendar, setPendingEventsCalendar] =
    useState<Calendar | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const selectedCalendars = useMemo(() => {
    return data?.filter((d) => selectedRows.includes(d.id)) ?? [];
  }, [data, selectedRows]);

  const columns: GridColDef<Calendar>[] = useMemo(
    () => [
      {
        field: 'summary',
        headerName: 'Calendar Name',
        flex: 1.5,
      },
      {
        field: 'id',
        headerName: 'ID',
        flex: 1.5,
      },
      {
        field: 'accessRole',
        headerName: 'Access Role',
        width: 150,
        type: 'singleSelect',
        valueOptions: ['Owner', 'Writer', 'Reader', 'Free/Busy Reader'],
        description: 'Access role of the searched for user.',
        valueGetter: (_value, row) => {
          switch (row.accessRole) {
            case 'owner':
              return 'Owner';
            case 'writer':
              return 'Writer';
            case 'reader':
              return 'Reader';
            case 'freeBusyReader':
              return 'Free/Busy Reader';
            default:
              return 'Unknown';
          }
        },
      },

      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1,
      },
      {
        field: 'timeZone',
        headerName: 'Time Zone',
        flex: 1,
      },
      {
        field: 'backgroundColor',
        headerName: 'Color',
        width: 80,
        filterable: false,
        display: 'flex',
        renderCell: (params) => {
          return (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                backgroundColor:
                  calendarColors.calendar[
                    params.row.colorId.toString() as keyof typeof calendarColors.calendar
                  ].background,
              }}
            ></Box>
          );
        },
      },
      {
        field: 'hidden',
        headerName: 'Is Shown',
        description: 'The calendar is hidden from the calendar list.',
        display: 'flex',
        type: 'boolean',
        valueGetter: (_value, row) => {
          return !row.hidden;
        },
      },
      {
        field: 'selected',
        headerName: 'Is Selected',
        editable: false,
        sortable: true,
        filterable: false,
        description:
          'Whether the calendar content shows up in the calendar UI.',
        display: 'flex',
        type: 'boolean',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params: GridRowParams) => {
          const buttonArray = [];

          const isOwnerOrEditor =
            params.row.accessRole === 'owner' ||
            params.row.accessRole === 'writer';

          if (isOwnerOrEditor) {
            buttonArray.push(
              <GridActionsCellItem
                label="Edit"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenEditCalendar(true);
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.AddShareToCalendar.value
            ) &&
            isOwnerOrEditor
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Share"
                icon={
                  <Tooltip arrow title="Share">
                    <Share />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenShareCalendar(true);
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.ViewCalendarEvents.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Events"
                icon={
                  <Tooltip arrow title="Events">
                    <CalendarIcon />
                  </Tooltip>
                }
                onClick={() => {
                  if (eventsGate.needsChangeLog) {
                    setPendingEventsCalendar(params.row);
                    eventsGate.openDialog();
                  } else {
                    setSelectedCalendar(params.row);
                    setOpenEventsCalendar(true);
                  }
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              administrationRoles,
              AdministrationRoles.ViewAppLogs.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="History"
                icon={
                  <Tooltip arrow title="History">
                    <History />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenCalendarHistory(true);
                }}
                showInMenu
              />
            );
          }

          return buttonArray;
        },
      },
    ],
    [
      calendarRoles,
      administrationRoles,
      eventsGate,
      setSelectedCalendar,
      setOpenEditCalendar,
      setOpenShareCalendar,
      setPendingEventsCalendar,
      setOpenEventsCalendar,
      setOpenCalendarHistory,
    ]
  );
  const showNoDataOverlayMessage = !fetched && isAllUsers && data.length === 0;

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        id: false,
      },
    },
  };

  const toolbarProps = useMemo(
    () => ({
      calendarRoles,
      setOpenAddCalendar,
      setOpenTransferCalendar,
      setOpenDeleteCalendars,
      setOpenQuickReports,
      userEmail,
      selectedCalendars: selectedCalendars,
    }),
    [calendarRoles, selectedCalendars, userEmail]
  );

  return (
    <>
      <SharedDataGridClient
        key="calendar-grid"
        slots={{
          toolbar: useToolbar(CalendarGridToolbar, toolbarProps),
          ...createGridOverlays(
            showNoDataOverlayMessage ? 'Search Above' : 'Calendars',
            <CalendarIcon />,
            'Search for a user to see their calendars.',
            'This user has no calendars.'
          ),
          ...(onOpenChangeLogDialog
            ? { noRowsOverlay: ChangeLogRequiredOverlay as never }
            : {}),
        }}
        slotProps={
          onOpenChangeLogDialog
            ? ({
                noRowsOverlay: {
                  onOpenDialog: onOpenChangeLogDialog,
                  permissionName: isAllUsers
                    ? CalendarRoles.ViewCalendars.name
                    : UserRoles.ViewCalendarsTab.name,
                },
              } as never)
            : undefined
        }
        defaultGridState={defaultGridState}
        gridKey={viewType.calendarsGridStates?.activeGridState}
        rows={data}
        columns={columns}
        loading={isLoading || isFetching}
        checkboxSelection
        disableRowSelectionOnClick
        isRowSelectable={(params: GridRowParams) =>
          params.row.accessRole === 'writer' ||
          params.row.accessRole === 'owner'
        }
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        gridStateKey="calendarsGridStates"
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
      />
      {userEmail !== '' && openAddCalendar && (
        <CalendarAddDialog
          open={openAddCalendar}
          handleClose={() => setOpenAddCalendar(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      )}
      {userEmail !== '' && selectedRows.length > 0 && openDeleteCalendars && (
        <CalendarDeleteDialog
          open={openDeleteCalendars}
          handleClose={() => setOpenDeleteCalendars(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
          selectedCalendars={selectedCalendars}
          setSelectedRows={setSelectedRows}
        />
      )}
      <ChangeLogDialog
        open={eventsGate.openChangeLogDialog}
        handleClose={eventsGate.dismiss}
        changeLogMessage={eventsGate.changeLogMessage}
        setChangeLogMessage={eventsGate.setChangeLogMessage}
        mutate={() =>
          eventsGate.confirm(() => {
            setSelectedCalendar(pendingEventsCalendar);
            setOpenEventsCalendar(true);
          })
        }
        mutateLoading={false}
      />
      {userEmail !== '' && selectedCalendar && openEventsCalendar && (
        <CalendarEventsDialog
          open={openEventsCalendar}
          handleClose={() => setOpenEventsCalendar(false)}
          userEmail={userEmail}
          selectedCalendar={selectedCalendar}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
          calendarRoles={calendarRoles}
          changeLogMessage={eventsGate.confirmedChangeLogMessage}
        />
      )}
      {openTransferCalendar && (
        <CalendarTransferDialog
          open={openTransferCalendar}
          handleClose={() => setOpenTransferCalendar(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      )}
      {userEmail !== '' && selectedCalendar && openEditCalendar && (
        <CalendarEditDialog
          open={openEditCalendar}
          handleClose={() => setOpenEditCalendar(false)}
          userEmail={userEmail}
          selectedCalendar={selectedCalendar}
          calendarRoles={calendarRoles}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      )}
      {userEmail !== '' && selectedCalendar && openShareCalendar && (
        <CalendarShareDialog
          open={openShareCalendar}
          handleClose={() => setOpenShareCalendar(false)}
          userEmail={userEmail}
          selectedCalendar={selectedCalendar}
          setSelectedCalendar={setSelectedCalendar}
          primaryDomain={primaryDomain}
          calendarRoles={calendarRoles}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      )}
      {selectedCalendar && openCalendarHistory ? (
        <CalendarHistoryDialog
          open={openCalendarHistory}
          handleClose={() => setOpenCalendarHistory(false)}
          selectedCalendar={selectedCalendar}
        />
      ) : null}
      <QuickReportsDialog
        open={openQuickReports}
        handleClose={() => setOpenQuickReports(false)}
        sourceData={userEmail ? [userEmail] : []}
        sourceType="User"
        dialogTitle="Calendars"
        analyticsEvent={
          analytics.user_management.user_details.calendars.quick_report
        }
        contentDescription={`Generate a one-time report for ALL of ${userEmail}'s calendars`}
        reports={reports}
        changeLogRoles={changeLogRoles}
      />
    </>
  );
}
